import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Form, Formik } from 'formik';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { MediumButton, OutlineButton, PrimaryButton } from '../../../components/Buttons';
import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from '../../../components/ModalElements';
import { Supply } from '../models/timeline';
import { SortableSupply } from './SortableSupply';

type SupplyWithId = Supply & { sortId?: number };
type FormValues = {
  supplies: SupplyWithId[];
};

const validationSchema = Yup.object({
  supplies: Yup.array().of(
    Yup.object({
      name: Yup.string()
        .required('Supply name is required')
        .max(64, 'Name is too long')
        .test('not-empty', 'Supply name cannot be empty', value => Boolean(value && value.trim() !== '')),
      quantity: Yup.string()
        .required('Quantity is required')
        .max(128, 'Quantity is too long')
        .test('not-empty', 'Quantity cannot be empty', value => Boolean(value && value.trim() !== '')),
    })
  ),
});

const ScrollableModalBody = styled(ModalBody)`
  max-height: 600px;
  overflow-y: auto;
`;

const AddSupplyContainer = styled.div`
  display: flex;
  justify-content: center;
`;

type EditSuppliesFormProps = {
  isSubmitting: boolean;
  handleDismiss: () => void;
  values: FormValues;
  setFieldValue: (field: string, value: any) => void;
  isValid: boolean;
};

function EditSuppliesForm({ isSubmitting, handleDismiss, values, setFieldValue, isValid }: EditSuppliesFormProps) {
  const isSubmittingState = isSubmitting;

  const hasValidSupplies = values.supplies.every(
    supply => supply.name && supply.name.trim() !== '' && supply.quantity && supply.quantity.trim() !== ''
  );

  const isSubmitDisabled = isSubmittingState || !isValid || !hasValidSupplies;

  const handleSupplyChange = (index: number, field: 'name' | 'quantity', value: string) => {
    const updatedSupplies = [...values.supplies];
    updatedSupplies[index] = {
      ...updatedSupplies[index],
      [field]: value,
    };
    setFieldValue('supplies', updatedSupplies);
  };

  const handleDeleteSupply = (index: number) => {
    const updatedSupplies = values.supplies.filter((_, i) => i !== index);
    setFieldValue('supplies', updatedSupplies);
  };

  const handleAddSupply = () => {
    const newSupply: Supply = {
      name: '',
      quantity: '1',
      supplyType: 'craft',
    };

    const supplyWithId = { ...newSupply, sortId: Date.now() + Math.random() };
    setFieldValue('supplies', [...values.supplies, supplyWithId]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = Number(active.id);
      const newIndex = Number(over.id);

      const updatedSupplies = arrayMove(values.supplies, oldIndex, newIndex);
      setFieldValue('supplies', updatedSupplies);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <>
      <ScrollableModalBody>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={values.supplies.map((_, index) => index)} strategy={verticalListSortingStrategy}>
            {values.supplies.map((supply, index) => (
              <SortableSupply
                key={supply.sortId || `supply-${index}`}
                supply={supply}
                index={index}
                onSupplyChange={handleSupplyChange}
                onDeleteSupply={handleDeleteSupply}
              />
            ))}
          </SortableContext>
        </DndContext>
        <AddSupplyContainer>
          <OutlineButton aria-label="Add supply" onClick={handleAddSupply} disabled={isSubmittingState}>
            <i className="fas fa-plus"></i> Add Supply
          </OutlineButton>
        </AddSupplyContainer>
      </ScrollableModalBody>

      <ModalFooter>
        <MediumButton onClick={handleDismiss} disabled={isSubmittingState}>
          Cancel
        </MediumButton>
        <PrimaryButton type="submit" disabled={isSubmitDisabled}>
          Save Supplies
        </PrimaryButton>
      </ModalFooter>
    </>
  );
}

export default function EditSuppliesModal({
  supplies,
  handleDismiss,
  isSubmitting,
  onSave,
}: {
  supplies: Supply[];
  handleDismiss: () => void;
  isSubmitting: boolean;
  onSave?: (supplies: Supply[]) => void;
}) {
  const getInitialValues = (): FormValues => ({
    supplies: supplies.map(supply => ({
      ...supply,
      name: supply.name || '',
      quantity: supply.quantity || '1',
      sortId: (supply as SupplyWithId).sortId || Date.now() + Math.random(),
    })),
  });

  const handleSubmit = (values: FormValues) => {
    if (onSave) {
      const suppliesToSave = values.supplies.map(({ sortId, ...supply }) => supply);
      onSave(suppliesToSave);
    }
    handleDismiss();
  };

  return (
    <ModalContainer dismissHandler={handleDismiss}>
      <ModalHeader>
        <h3>Edit Supplies</h3>
      </ModalHeader>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, setFieldValue, isSubmitting: formikIsSubmitting, isValid }) => (
          <Form>
            <EditSuppliesForm
              isSubmitting={isSubmitting || formikIsSubmitting}
              handleDismiss={handleDismiss}
              values={values}
              setFieldValue={setFieldValue}
              isValid={isValid}
            />
          </Form>
        )}
      </Formik>
    </ModalContainer>
  );
}
